import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { PageHeader, Table, Td } from "../../components/dashboard/Shared";

const SEVERITY_META = {
  high: { color: "text-[#FF3B30]", bg: "bg-[#FF3B30]/10 border-[#FF3B30]/30", dot: "bg-[#FF3B30]" },
  medium: { color: "text-[#FFD600]", bg: "bg-[#FFD600]/10 border-[#FFD600]/30", dot: "bg-[#FFD600]" },
  low: { color: "text-[#00F0FF]", bg: "bg-[#00F0FF]/10 border-[#00F0FF]/30", dot: "bg-[#00F0FF]" },
};

const TYPE_LABELS = {
  metaapi_disconnect: "MetaApi Disconnect",
  mt5_sync_fail: "MT5 Sync Failure",
  drawdown: "Drawdown Breach",
  kyc_pending: "KYC Pending",
  pending_profit_share: "Pending Profit Share",
};

export default function Alerts() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/alerts");
      setData(data);
    } catch { setData({ count: 0, alerts: [] }); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const rows = data ? data.alerts.filter((a) => !filter || a.type === filter || a.severity === filter) : [];

  return (
    <div>
      <PageHeader title="System Alerts" sub="Drawdown, MT5 sync, KYC, profit share, MetaApi issues">
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="alerts-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="input-dark !w-52 !py-1.5 text-xs">
            <option value="">All alerts</option>
            <option value="high">High severity only</option>
            <option value="medium">Medium severity only</option>
            <option value="low">Low severity only</option>
            <option value="drawdown">Drawdown</option>
            <option value="metaapi_disconnect">MetaApi disconnects</option>
            <option value="mt5_sync_fail">MT5 sync failures</option>
            <option value="kyc_pending">KYC pending</option>
            <option value="pending_profit_share">Pending fees</option>
          </select>
          <button onClick={load} disabled={busy} data-testid="alerts-refresh-button" className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2 disabled:opacity-50">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </PageHeader>

      {!data ? <div className="skeleton h-72" /> :
        rows.length === 0 ? (
          <div className="card-dark p-12 text-center" data-testid="no-alerts-state">
            <AlertTriangle size={28} className="text-[#00E676] mx-auto mb-3" />
            <p className="text-gray-300">All clear.</p>
            <p className="text-sm text-gray-500 mt-2">No {filter || "system"} alerts right now.</p>
          </div>
        ) : (
          <Table headers={["Severity", "Type", "Client", "Message", "When"]} testId="alerts-table">
            {rows.map((a) => {
              const meta = SEVERITY_META[a.severity] || SEVERITY_META.low;
              return (
                <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`alert-row-${a.id}`}>
                  <Td><span className={`inline-flex items-center gap-2 text-[10px] px-2 py-1 rounded uppercase tracking-wider ${meta.bg} ${meta.color}`}><span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />{a.severity}</span></Td>
                  <Td className="text-xs text-white">{TYPE_LABELS[a.type] || a.type}</Td>
                  <Td>
                    {a.user_id ? (
                      <Link to={`/admin/users/${a.user_id}`} className="text-white hover:text-[#00F0FF]" data-testid={`alert-user-link-${a.id}`}>{a.user_name || a.user_email}</Link>
                    ) : "—"}
                    <p className="text-xs text-gray-500">{a.user_email}</p>
                  </Td>
                  <Td className="text-xs text-gray-400 !whitespace-normal max-w-md"><span className="text-white font-medium">{a.title}</span> — {a.message}</Td>
                  <Td className="tabular text-xs text-gray-500">{a.created_at ? String(a.created_at).slice(0, 16).replace("T", " ") : "—"}</Td>
                </tr>
              );
            })}
          </Table>
        )}
    </div>
  );
}
